import React from 'react';
import classnames from 'classnames';
import { mixpanel } from '@fiverr-private/obs';
import { BriefCreationDrawer } from '@fiverr-private/dm_brief_creation';
import { canViewBusinessLink } from '@fiverr-private/layout_menus';
import { LazySourcingDomainSelectionDrawer } from '@fiverr-private/expert_sourcing_domain_selection_drawer';
import { user, rollouts, localeSettings } from '../../PropTypes';
import { usePricingDataContext } from '../../contexts/PricingDataContext';
import { HEADER_NAV_SOURCE } from '../../constants';
import withContext from '../../lib/withContext';
import Nav from '../../lib/Nav';
import If from '../../lib/If';
import {
    BuyerAccountSettingsLink,
    BuyerBillingLink,
    BuyerBecomeSellerLink,
    BuyerHelpLink,
    BuyerListsLink,
    BuyerProfileLink,
    BuyerReferalLink,
    BuyerSwitchToSellerLink,
    ShareIdeasLink,
    BuyerYourBriefsLink,
    BuyerBriefCreationLink,
} from '../../components/BuyerLinks';
import Layout from '../../components/Layout';
import MenuLocaleSettingsLink from '../../components/LocaleSettingsLinks/MenuLocaleSettingsLink';
import { Logo } from '../../components/Logo';
import LogoutLink from '../../components/LogoutLink';
import MainRow from '../../components/MainRow';
import Messages from '../../components/Messages';
import Navicon from '../../components/Navicon';
import Orders from '../../components/Orders';
import Search from '../../components/Search';
import UserAvatar from '../../components/UserAvatar';
import UserBalanceTag from '../../components/UserBalanceTag';
import { ChatTooltipWrapper } from '../../components/ChatTooltipWrapper';
import { SellerDashboardLink } from '../../components/SellerLinks';
import { BusinessProSolutionsNavigation } from '../../components/BusinessProSolutionsNavigation';
import ProFeatures from '../../components/ProFeatures/ProFeatures';
import { sendBigQueryEvent } from '../../utils/bigQuery';
import { TeamDashboardInviteLink, TeamDashboardLink } from '../TeamAccountMember/MemberLinks';
import { showOrders } from './utils/showOrders';

import './style.scss';
import styles from './styles.module.scss';

export const Buyer = (_props, { user, rollouts, currency }) => {
    const pricingData = usePricingDataContext();
    const {
        inShowOrdersNavigatorToRestrictedUsersRollout,
        inRvDataFetchingSearchBarRollout,
        inDolphinsJobPostMigration,
        inDolphinsRewardsLaunch,
        inLynxesMustangsDmBriefCreationLayoutComponentRollout,
        inMustangsPersonBriefLayout,
    } = rollouts;

    const {
        id,
        isSeller,
        username,
        fullName,
        isActive,
        isPending,
        isRnc,
        isOnline,
        profileImage,
        balance = '',
        isReferralEligible,
        isVid,
    } = user;

    const isTeamAccountAdmin = user.teamAccount?.slug === username;

    const reportAvatarMenuOpen = () => {
        sendBigQueryEvent({
            group: 'header_clicks',
            type: 'clicked_header.open_avatar_menu',
            user: {
                id: user.id,
                navigation_mode: 'buying',
            },
        });

        mixpanel.track('Avatar menu clicked');
    };

    return (
        <Layout className={classnames('buyer-header')}>
            <MainRow>
                <Navicon />
                <Logo />
                <Search rollouts={rollouts} inRvDataFetchingSearchBarRollout={inRvDataFetchingSearchBarRollout} />
                <Nav alignToRight className={classnames(styles.navLinks, 'nav-with-space')}>
                    <If condition={!isSeller && canViewBusinessLink()}>
                        <Nav.Item className={styles.navButton} displayFromLG>
                            <BusinessProSolutionsNavigation />
                        </Nav.Item>
                    </If>
                    <Nav.Item className={styles.navLinkIcon} displayFromMD>
                        <Messages username={username} showAsIcons drawerType={Messages.DRAWER_TYPES.NOTIFICATIONS} />
                    </Nav.Item>
                    <Nav.Item className={classnames(styles.navLinkIcon, styles.displayMd)} displayFromMD>
                        <ChatTooltipWrapper>
                            <Messages username={username} showAsIcons drawerType={Messages.DRAWER_TYPES.MESSAGES} />
                        </ChatTooltipWrapper>
                    </Nav.Item>
                    <Nav.Item displayFromLG>
                        <BuyerListsLink tooltipClassName={styles.listIconTooltip} buyerId={id} showAsIcon={true} />
                    </Nav.Item>
                    <If
                        condition={showOrders({
                            isActive,
                            isPending,
                            isRnc,
                            inShowOrdersNavigatorToRestrictedUsersRollout,
                        })}
                    >
                        <Nav.Item displayFromLG>
                            <Orders />
                        </Nav.Item>
                    </If>
                    <If condition={isSeller}>
                        <Nav.Item displayFromMD>
                            <BuyerSwitchToSellerLink buyerId={id} buyerUsername={username} />
                        </Nav.Item>
                    </If>
                    <Nav.Item displayFromSM>
                        <Nav.PopoverItems
                            placement="left"
                            onOpen={reportAvatarMenuOpen}
                            target={
                                <UserAvatar
                                    fullName={fullName}
                                    username={username}
                                    profileImage={profileImage}
                                    isOnline={isOnline}
                                />
                            }
                        >
                            <Nav.PopoverItem>
                                <BuyerProfileLink buyerId={id} />
                            </Nav.PopoverItem>
                            <Nav.PopoverItem>
                                <BuyerBriefCreationLink currency={currency} />
                            </Nav.PopoverItem>
                            <Nav.PopoverItem>
                                <BuyerYourBriefsLink />
                            </Nav.PopoverItem>
                            {isSeller && (
                                <Nav.PopoverItem>
                                    <SellerDashboardLink sellerId={id} sellerUsername={username} />
                                </Nav.PopoverItem>
                            )}
                            <If condition={!!user.teamAccount}>
                                <Nav.PopoverItem>
                                    <TeamDashboardLink teamOwnerUsername={user.teamAccount?.slug} />
                                </Nav.PopoverItem>

                                <If condition={isTeamAccountAdmin}>
                                    <Nav.PopoverItem>
                                        <TeamDashboardInviteLink teamOwnerUsername={user.teamAccount?.slug} />
                                    </Nav.PopoverItem>
                                </If>
                            </If>
                            <If condition={isReferralEligible}>
                                <Nav.PopoverItem>
                                    <BuyerReferalLink buyerId={id} />
                                </Nav.PopoverItem>
                            </If>
                            <Nav.PopoverDivider />
                            <If condition={!isSeller}>
                                <Nav.PopoverItem>
                                    <BuyerBecomeSellerLink buyerId={id} source={HEADER_NAV_SOURCE} />
                                </Nav.PopoverItem>
                            </If>
                            <Nav.PopoverItem>
                                <BuyerAccountSettingsLink buyerId={id} />
                            </Nav.PopoverItem>
                            <Nav.PopoverItem>
                                <BuyerBillingLink buyerId={id} />
                            </Nav.PopoverItem>
                            <Nav.PopoverDivider />
                            <ProFeatures />
                            <Nav.PopoverDivider />
                            <MenuLocaleSettingsLink />
                            <Nav.PopoverItem>
                                <BuyerHelpLink buyerId={id} />
                            </Nav.PopoverItem>
                            {isVid && (
                                <Nav.PopoverItem>
                                    <ShareIdeasLink buyerId={user.id} />
                                </Nav.PopoverItem>
                            )}
                            <Nav.PopoverDivider />
                            <Nav.PopoverItem>
                                <LogoutLink />
                            </Nav.PopoverItem>
                        </Nav.PopoverItems>
                    </Nav.Item>
                    <If condition={!!balance}>
                        <Nav.Item displayFromSM>
                            <UserBalanceTag balance={balance} />
                        </Nav.Item>
                    </If>
                </Nav>
            </MainRow>
            <LazySourcingDomainSelectionDrawer
                currency={currency}
                isPriceUpfrontEnabled={pricingData?.isPriceUpfrontEnabled}
                priceUpfrontConfiguration={pricingData?.priceUpfrontConfiguration}
                priceUpfrontContext={pricingData?.priceUpfrontContext}
            />
            {inLynxesMustangsDmBriefCreationLayoutComponentRollout ? null : (
                <BriefCreationDrawer
                    rollouts={{
                        inDolphinsJobPostMigration,
                        inDolphinsRewardsLaunch,
                        inMustangsPersonBrief: inMustangsPersonBriefLayout,
                    }}
                    enableEnhancedUx
                />
            )}
        </Layout>
    );
};

Buyer.contextTypes = {
    user,
    rollouts,
    currency: localeSettings.currency,
};

/**
 * @param {HeaderProps} props
 * @returns {React.ReactElement} Header
 */
export default withContext(Buyer);
